/**
 * The drawer's seed + dirty logic: seed once per key (background refetches
 * never clobber edits), dirty = JSON compare against the seeded baseline.
 * A new editor seeds from `fresh` (its campaign's preset) once `pending`
 * clears — the preset master's config may still be loading.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useEffect, useMemo, useRef } from 'react';

import type { ContainerDraft } from '../../../drop-generator.types';
import type { useContainerDetail } from '../../../useDropGenerator';
import type { EditorTabKey } from '../EditorTabs';
import { apiToDraft } from '../state/container-draft';
import type { ContainerEditor } from '../state/useContainerEditor';

interface Options {
  editor: ContainerEditor;
  detail: ReturnType<typeof useContainerDetail>;
  containerId: string | null;
  seedKey: string;
  /** What a NEW editor starts from (see state/master/new-draft.ts). */
  fresh: ContainerDraft;
  /** The preset is still loading — do not seed yet. */
  pending: boolean;
  initialTab: EditorTabKey | undefined;
  setTab: (tab: EditorTabKey) => void;
}

export function useEditorSeed(o: Options) {
  const { editor, detail, containerId, seedKey, fresh, pending, initialTab, setTab } = o;
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededKeyRef.current === seedKey) return;
    if (containerId ? !detail.data : pending) return;
    seededKeyRef.current = seedKey;
    editor.reset(detail.data ? apiToDraft(detail.data) : fresh);
    setTab(initialTab ?? 'general');
  }, [seedKey, containerId, detail.data, pending, fresh, editor]);
  const baseline = useMemo(
    () => JSON.stringify(detail.data ? apiToDraft(detail.data) : fresh),
    [detail.data, fresh],
  );
  const serialized = useMemo(() => JSON.stringify(editor.draft), [editor.draft]);
  return { dirty: seededKeyRef.current === seedKey && serialized !== baseline };
}
